import { Analytics } from '@easy-expense/analytics-client';
import {
  currentUser,
  getApproveReportFn,
  getUndoReportApprovalFn,
  getReimburseReportFn,
  getUndoReportReimbursementFn,
} from '@easy-expense/auth-client';
import {
  useCachedMembers,
  useIsAdminOrManagerRole,
  useWorkspaceKeysStore,
  useWorkspaceSettings,
} from '@easy-expense/data-firestore-client';
import { Report, ReportSendStatus } from '@easy-expense/data-schema-v2';
import Data from '@easy-expense/frontend-data-layer';
import { getTranslation, useIntlStore } from '@easy-expense/intl-client';
import { Icon } from '@easy-expense/ui-shared-components';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { ReportDisplayStatus, sendStatusToDisplayStatus } from '@easy-expense/utils-client';
import { dateFormatSkeleton, getUserDisplayName, isReportEmpty } from '@easy-expense/utils-shared';
import { Popover } from '@mui/material';
import React from 'react';

import { Button } from '../Button.components';

const MAX_REJECTION_NOTE_LENGTH = 500;

type ReportApprovalReport = Pick<
  Report,
  | 'key'
  | 'status'
  | 'approvalStatus'
  | 'statusInfo'
  | 'expenses'
  | 'trips'
  | 'incomes'
  | 'createdBy'
  | 'shared'
>;

type SendStatusDisplay = Extract<ReportDisplayStatus, 'active' | 'sent' | 'complete'>;
const SEND_STATUS_DISPLAY_OPTIONS: SendStatusDisplay[] = ['active', 'sent', 'complete'];
const DISPLAY_TO_SEND_STATUS: Record<SendStatusDisplay, ReportSendStatus> = {
  active: 'Unsent',
  sent: 'Sent',
  complete: 'Complete',
};

function getStatusMeta(status: ReportDisplayStatus): {
  label: string;
  icon: string;
  bg: 'warningXLight' | 'successXLight' | 'destructiveXLight' | 'brandPrimaryXLight' | 'grayXLight';
  border: 'warning' | 'success' | 'destructive' | 'brandPrimary' | 'gray';
  color: string;
} {
  if (status === 'approved') {
    return {
      label: getTranslation('Approved'),
      icon: 'checkmark-circle',
      bg: 'successXLight',
      border: 'success',
      color: theme.colors.successDark,
    };
  }
  if (status === 'rejected') {
    return {
      label: getTranslation('Rejected'),
      icon: 'close-circle',
      bg: 'destructiveXLight',
      border: 'destructive',
      color: theme.colors.destructiveDark,
    };
  }
  if (status === 'active') {
    return {
      label: getTranslation('Active'),
      icon: 'document-text-outline',
      bg: 'grayXLight',
      border: 'gray',
      color: theme.colors.grayDark,
    };
  }
  if (status === 'sent') {
    return {
      label: getTranslation('Sent'),
      icon: 'send-outline',
      bg: 'warningXLight',
      border: 'warning',
      color: theme.colors.warningDark,
    };
  }
  if (status === 'complete') {
    return {
      label: getTranslation('Complete'),
      icon: 'checkmark-done-outline',
      bg: 'successXLight',
      border: 'success',
      color: theme.colors.successDark,
    };
  }
  return {
    label: getTranslation('Pending Approval'),
    icon: 'time-outline',
    bg: 'warningXLight',
    border: 'warning',
    color: theme.colors.warningDark,
  };
}

function setReportWorkflowStatus(reportKey: string, status: ReportSendStatus) {
  Data.reports.update(reportKey, { status });
}

/** EZ-4884: an empty report has nothing for a manager to review, so it can't enter the queue. */
function alertEmptyReport() {
  alert(getTranslation('Please add something to the report first'));
}

function rejectReport(reportKey: string, reason: string) {
  const rejectedBy = currentUser()?.uid;
  if (!rejectedBy) {
    return;
  }
  const previousStatusInfo = Data.reports.getByKey(reportKey)?.statusInfo;
  Data.reports.update(reportKey, {
    approvalStatus: 'rejected',
    statusInfo: { ...previousStatusInfo, rejected: { by: rejectedBy, date: Date.now(), reason } },
  });
  Analytics.track('report_rejected', { reportKey, rejectedBy });
}

// Admin-initiated undo, distinct from resubmitReport (member-initiated): keeps the original
// statusInfo.pending (who submitted it and when) instead of stamping a new submission event.
function undoReportRejection(reportKey: string) {
  const undoneBy = currentUser()?.uid;
  if (!undoneBy) {
    return;
  }
  const previousStatusInfo = Data.reports.getByKey(reportKey)?.statusInfo;
  Data.reports.update(reportKey, {
    approvalStatus: 'pending',
    statusInfo: { ...previousStatusInfo, rejected: null },
  });
  Analytics.track('report_rejection_undone', { reportKey, undoneBy });
}

/**
 * Undoing a submission pulls the report back out of the approval flow entirely, and the link
 * sits right above Reject/Approve — so confirm first rather than let a stray click do it.
 */
function undoReportSubmission(reportKey: string) {
  const undoneBy = currentUser()?.uid;
  if (!undoneBy) {
    return;
  }
  if (!confirm(getTranslation("Are you sure you want to undo this report's submission?"))) {
    return;
  }
  const previousStatusInfo = Data.reports.getByKey(reportKey)?.statusInfo;
  Data.reports.update(reportKey, {
    approvalStatus: null,
    statusInfo: { ...previousStatusInfo, pending: null },
  });
  Analytics.track('report_submission_undone', { reportKey, undoneBy });
}

export const ReportApprovalSection: React.FC<{
  report: ReportApprovalReport;
  onSaveReport: (overrides?: Partial<Report>) => void;
}> = ({ report, onSaveReport }) => {
  const isManagerOrAdmin = useIsAdminOrManagerRole();
  const submittedByCurrentUser = report.statusInfo?.pending?.by === currentUser()?.uid;
  const members = useCachedMembers();
  const { formatCurrency, formatDate } = useIntlStore();
  const { workspaceSettings } = useWorkspaceSettings();
  const requiresApproval = workspaceSettings?.requiresApproval ?? false;
  const [showRejectInput, setShowRejectInput] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState<HTMLElement | null>(null);
  const orgID = useWorkspaceKeysStore((s) => s.currentOrganizationKey);
  const workspaceID = useWorkspaceKeysStore((s) => s.currentWorkspaceKey);

  const reportExpenses = report.expenses
    .map((expenseKey) => Data.expenses.getByKey(expenseKey))
    .filter((expense): expense is NonNullable<typeof expense> => !!expense);

  function submitReportForApproval() {
    const submittedBy = currentUser()?.uid;
    if (!submittedBy) {
      return;
    }
    if (isReportEmpty(report)) {
      alertEmptyReport();
      return;
    }
    onSaveReport({
      approvalStatus: 'pending',
      statusInfo: {
        ...report.statusInfo,
        pending: { by: submittedBy, date: Date.now() },
        rejected: null,
      },
    });
    Analytics.track('report_submitted_for_approval', {
      reportKey: report.key,
      submittedBy,
      expenseCount: report.expenses.length,
    });
  }

  function resubmitReport() {
    const resubmittedBy = currentUser()?.uid;
    if (!resubmittedBy) {
      return;
    }
    if (isReportEmpty(report)) {
      alertEmptyReport();
      return;
    }
    onSaveReport({
      approvalStatus: 'pending',
      statusInfo: {
        ...report.statusInfo,
        pending: { by: resubmittedBy, date: Date.now() },
        rejected: null,
      },
    });
    Analytics.track('report_resubmitted', { reportKey: report.key, resubmittedBy });
  }

  // Optimistic UI (EZ-4841): these four actions call a slow server callable (they cascade to
  // child expenses in an atomic batch, which the client isn't allowed to do directly). Rather
  // than block the UI on that round trip, apply the same patch locally first — the callable
  // still runs and is the source of truth, but the UI reflects the change immediately. Not the
  // most correct architecture (a failed callable leaves the optimistic guess until corrected by
  // the next real write), but it's the tradeoff asked for to kill the perceived lag.
  function approveReport() {
    const approvedBy = currentUser()?.uid;
    if (!approvedBy) {
      return;
    }
    const approvedAt = Date.now();
    const eligibleExpenses = reportExpenses.filter(
      (expense) => expense.approvalStatus === 'pending' && !expense.approvalSource,
    );

    Data.reports.update(report.key, {
      approvalStatus: 'approved',
      statusInfo: { ...report.statusInfo, approved: { by: approvedBy, date: approvedAt } },
    });
    eligibleExpenses.forEach((expense) => {
      Data.expenses.update(
        expense.key,
        {
          approvalStatus: 'approved',
          approvalSource: { approvedBy, approvedAt, sourceReportKey: report.key },
        },
        undefined,
        undefined,
        'approval',
      );
    });

    getApproveReportFn()({ orgID, workspaceID, reportKey: report.key }).catch((error) => {
      console.error('Error approving report', error);
      alert(getTranslation('Error approving report. Please try again.'));
    });
  }

  function undoReportApproval() {
    const undoneBy = currentUser()?.uid;
    if (!undoneBy) {
      return;
    }
    const revertedExpenses = reportExpenses.filter(
      (expense) => expense.approvalSource?.sourceReportKey === report.key,
    );

    Data.reports.update(report.key, {
      approvalStatus: 'pending',
      statusInfo: { ...report.statusInfo, approved: null },
    });
    revertedExpenses.forEach((expense) => {
      Data.expenses.update(
        expense.key,
        { approvalStatus: 'pending', approvalSource: null },
        undefined,
        undefined,
        'approval',
      );
    });

    getUndoReportApprovalFn()({ orgID, workspaceID, reportKey: report.key }).catch((error) => {
      console.error('Error undoing report approval', error);
      alert(getTranslation('Error undoing report approval. Please try again.'));
    });
  }

  function reimburseReport() {
    const reimbursedBy = currentUser()?.uid;
    if (!reimbursedBy) {
      return;
    }
    const reimbursedAt = Date.now();
    const eligibleExpenses = reportExpenses.filter(
      (expense) =>
        expense.approvalStatus === 'approved' &&
        expense.isReimbursable &&
        expense.reimbursableState !== 'reimbursed' &&
        !expense.reimbursementSource,
    );

    Data.reports.update(report.key, {
      statusInfo: { ...report.statusInfo, reimbursed: { by: reimbursedBy, date: reimbursedAt } },
    });
    eligibleExpenses.forEach((expense) => {
      Data.expenses.update(
        expense.key,
        {
          reimbursableState: 'reimbursed',
          reimbursementSource: { reimbursedBy, reimbursedAt, sourceReportKey: report.key },
        },
        undefined,
        undefined,
        'approval',
      );
    });

    getReimburseReportFn()({ orgID, workspaceID, reportKey: report.key }).catch((error) => {
      console.error('Error reimbursing report', error);
      alert(getTranslation('Error reimbursing report. Please try again.'));
    });
  }

  function undoReportReimbursement() {
    const undoneBy = currentUser()?.uid;
    if (!undoneBy) {
      return;
    }
    const revertedExpenses = reportExpenses.filter(
      (expense) => expense.reimbursementSource?.sourceReportKey === report.key,
    );

    Data.reports.update(report.key, {
      statusInfo: { ...report.statusInfo, reimbursed: null },
    });
    revertedExpenses.forEach((expense) => {
      Data.expenses.update(
        expense.key,
        {
          reimbursableState: 'notReimbursed',
          reimbursementSource: null,
        },
        undefined,
        undefined,
        'approval',
      );
    });

    getUndoReportReimbursementFn()({ orgID, workspaceID, reportKey: report.key }).catch((error) => {
      console.error('Error undoing report reimbursement', error);
      alert(getTranslation('Error undoing report reimbursement. Please try again.'));
    });
  }

  function memberName(userID?: string) {
    return getUserDisplayName(members.find((m) => m.key === userID));
  }

  function formatShortDate(millis: number) {
    return formatDate(new Date(millis), { skeleton: dateFormatSkeleton.abbreviated });
  }

  function selectStatusOption(onSelect: () => void) {
    setStatusMenuAnchor(null);
    onSelect();
  }

  const reimbursableExpenses = reportExpenses.filter((expense) => expense.isReimbursable);
  const unreimbursedExpenses = reimbursableExpenses.filter(
    (expense) => expense.reimbursableState !== 'reimbursed',
  );
  const needsReimbursement = unreimbursedExpenses.length > 0;
  const hasBeenReimbursed =
    reimbursableExpenses.length > 0 &&
    reimbursableExpenses.every((expense) => expense.reimbursableState === 'reimbursed');
  const wasReimbursedViaReport = reimbursableExpenses.some(
    (expense) => expense.reimbursementSource?.sourceReportKey === report.key,
  );
  const reimbursementDueTotal = unreimbursedExpenses.reduce(
    (sum, expense) => sum + Math.abs(expense.total),
    0,
  );
  const reimbursementSource = reimbursableExpenses.find(
    (expense) => expense.reimbursementSource,
  )?.reimbursementSource;

  const sendStatusDisplay = sendStatusToDisplayStatus(report.status);
  const sendStatusMeta = getStatusMeta(sendStatusDisplay);
  const sendStatusOptions = SEND_STATUS_DISPLAY_OPTIONS.filter(
    (option) => option !== sendStatusDisplay,
  ).map((option) => ({
    status: option,
    onSelect: () =>
      selectStatusOption(() => setReportWorkflowStatus(report.key, DISPLAY_TO_SEND_STATUS[option])),
  }));
  const canOpenSendStatusMenu = sendStatusOptions.length > 0;

  const approvalMeta = report.approvalStatus ? getStatusMeta(report.approvalStatus) : null;

  return (
    <Layout.Column>
      <OpenSans.Secondary weight="bold-700" size="xs-12">
        {getTranslation('Status')}
      </OpenSans.Secondary>
      <Spacer.Vertical size={4} />

      {/* Once a report has an approvalStatus, that's treated as THE status — the send-status
          pill (Active/Sent/Complete) is hidden entirely rather than shown alongside it, so a
          pending/rejected/approved report never shows two status pills at once. */}
      {!report.approvalStatus ? (
        <>
          <Layout.PressableRow
            align
            justify="space-between"
            px
            py={16}
            bg={sendStatusMeta.bg}
            border={[2, 'solid', sendStatusMeta.border]}
            radius
            style={{ width: '100%', cursor: canOpenSendStatusMenu ? 'pointer' : 'default' }}
            onClick={
              canOpenSendStatusMenu
                ? (e: React.MouseEvent<HTMLElement>) => setStatusMenuAnchor(e.currentTarget)
                : undefined
            }
          >
            <Layout.Row align>
              <Icon
                name={sendStatusMeta.icon}
                size={20}
                color={theme.colors[sendStatusMeta.border]}
              />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary weight="bold-700" style={{ color: sendStatusMeta.color }}>
                {sendStatusMeta.label}
              </OpenSans.Primary>
            </Layout.Row>
            {canOpenSendStatusMenu ? (
              <Icon name="chevron-down-outline" size={18} color={sendStatusMeta.color} />
            ) : null}
          </Layout.PressableRow>

          {canOpenSendStatusMenu ? (
            <Popover
              open={Boolean(statusMenuAnchor)}
              anchorEl={statusMenuAnchor}
              onClose={() => setStatusMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Layout.Column py={4} style={{ minWidth: 240 }}>
                <Layout.Row align bg="white" px={16} py={10} style={{ cursor: 'default' }}>
                  <Icon
                    name={sendStatusMeta.icon}
                    size={18}
                    color={theme.colors[sendStatusMeta.border]}
                  />
                  <Spacer.Horizontal size={8} />
                  <OpenSans.Primary size="s-16" style={{ color: sendStatusMeta.color }}>
                    {sendStatusMeta.label}
                  </OpenSans.Primary>
                  <Spacer.Flex />
                  <Icon name="checkmark" size={16} color={sendStatusMeta.color} />
                </Layout.Row>
                {sendStatusOptions.map((option) => {
                  const optionMeta = getStatusMeta(option.status);
                  return (
                    <Layout.PressableRow
                      key={option.status}
                      align
                      bg="white"
                      px={16}
                      py={10}
                      style={{ cursor: 'pointer' }}
                      onClick={option.onSelect}
                    >
                      <Icon
                        name={optionMeta.icon}
                        size={18}
                        color={theme.colors[optionMeta.border]}
                      />
                      <Spacer.Horizontal size={8} />
                      <OpenSans.Primary size="s-16" style={{ color: optionMeta.color }}>
                        {optionMeta.label}
                      </OpenSans.Primary>
                    </Layout.PressableRow>
                  );
                })}
              </Layout.Column>
            </Popover>
          ) : null}
        </>
      ) : null}

      {!report.approvalStatus && !isManagerOrAdmin && !report.shared && requiresApproval ? (
        <>
          <Spacer.Vertical size={12} />
          <Button.Primary
            variant="bold"
            py={12}
            align="center"
            content={getTranslation('Submit for Approval')}
            style={{ width: '100%' }}
            onClick={() => submitReportForApproval()}
          />
        </>
      ) : null}

      {report.approvalStatus && approvalMeta ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.Column
            px
            py
            bg={approvalMeta.bg}
            border={[2, 'solid', approvalMeta.border]}
            radius
          >
            <Layout.Row align>
              <Icon name={approvalMeta.icon} size={20} color={theme.colors[approvalMeta.border]} />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary weight="bold-700" style={{ color: approvalMeta.color }}>
                {approvalMeta.label}
              </OpenSans.Primary>
            </Layout.Row>

            {report.approvalStatus === 'pending' && report.statusInfo?.pending ? (
              <>
                <Spacer.Vertical size={4} />
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Submitted by')} {memberName(report.statusInfo.pending.by)}{' '}
                  {getTranslation('on')} {formatShortDate(report.statusInfo.pending.date)}
                </OpenSans.Secondary>
              </>
            ) : null}
            {report.approvalStatus === 'rejected' && report.statusInfo?.rejected ? (
              <>
                <Spacer.Vertical size={4} />
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Rejected by')} {memberName(report.statusInfo.rejected.by)}{' '}
                  {getTranslation('on')} {formatShortDate(report.statusInfo.rejected.date)}
                  {report.statusInfo.rejected.reason
                    ? ` — "${report.statusInfo.rejected.reason}"`
                    : ''}
                </OpenSans.Secondary>
              </>
            ) : null}
            {report.approvalStatus === 'approved' && report.statusInfo?.approved ? (
              <>
                <Spacer.Vertical size={4} />
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Approved by')} {memberName(report.statusInfo.approved.by)}{' '}
                  {getTranslation('on')} {formatShortDate(report.statusInfo.approved.date)}
                </OpenSans.Secondary>
              </>
            ) : null}
          </Layout.Column>
        </>
      ) : null}

      {report.approvalStatus === 'pending' && (isManagerOrAdmin || submittedByCurrentUser) ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow bg="transparent" onClick={() => undoReportSubmission(report.key)}>
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="s-16">{getTranslation('Undo submission')}</OpenSans.Secondary>
          </Layout.PressableRow>
        </>
      ) : null}

      {report.approvalStatus === 'approved' && isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow bg="transparent" onClick={() => undoReportApproval()}>
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="s-16">{getTranslation('Undo approval')}</OpenSans.Secondary>
          </Layout.PressableRow>
        </>
      ) : null}

      {report.approvalStatus === 'rejected' && isManagerOrAdmin ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow bg="transparent" onClick={() => undoReportRejection(report.key)}>
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Secondary size="s-16">{getTranslation('Undo rejection')}</OpenSans.Secondary>
          </Layout.PressableRow>
        </>
      ) : null}

      {report.approvalStatus === 'pending' && isManagerOrAdmin && !showRejectInput ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Row>
            <Button.DestructiveOutline
              variant="bold"
              py={12}
              align="center"
              content={getTranslation('Reject')}
              style={{ flex: 1 }}
              onClick={() => setShowRejectInput(true)}
            />
            <Spacer.Horizontal size={12} />
            <Button.Success
              variant="bold"
              py={12}
              align="center"
              style={{ flex: 3 }}
              onClick={() => approveReport()}
            >
              <Icon name="checkmark-outline" size={18} color={theme.colors.buttonWhite} />
              <Spacer.Horizontal size={6} />
              <OpenSans.Custom
                size="m-18"
                weight="bold-700"
                style={{ color: theme.colors.buttonWhite }}
              >
                {getTranslation('Approve')}
              </OpenSans.Custom>
            </Button.Success>
          </Layout.Row>
        </>
      ) : null}

      {report.approvalStatus === 'pending' && isManagerOrAdmin && showRejectInput ? (
        <>
          <Spacer.Vertical size={12} />
          <OpenSans.Custom size="xs-12" weight="bold-700" color="destructiveDark">
            {getTranslation('Reason for rejection')}
          </OpenSans.Custom>
          <Spacer.Vertical size={6} />
          <Layout.Row
            border={[1, 'solid', 'destructive']}
            radius={4}
            bg="destructiveXLight"
            px
            py={8}
            style={{ minHeight: 80 }}
          >
            <textarea
              name="rejectReason"
              value={rejectReason}
              placeholder={getTranslation('Reason for rejection')}
              rows={3}
              autoFocus
              maxLength={MAX_REJECTION_NOTE_LENGTH}
              style={{
                flexGrow: 1,
                width: '100%',
                border: 'none',
                outline: 'none',
                resize: 'vertical',
                backgroundColor: 'transparent',
                fontFamily: 'inherit',
                fontSize: 14,
              }}
              onChange={(event) =>
                setRejectReason(event.currentTarget.value.slice(0, MAX_REJECTION_NOTE_LENGTH))
              }
            />
          </Layout.Row>
          {rejectReason.length >= MAX_REJECTION_NOTE_LENGTH ? (
            <>
              <Spacer.Vertical size={6} />
              <OpenSans.Custom size="xs-12" color="destructiveDark">
                {getTranslation('You have reached the 500 character limit.')}
              </OpenSans.Custom>
            </>
          ) : null}
          <Spacer.Vertical size={12} />
          <Layout.Row>
            <Button.Outline
              variant="bold"
              align="center"
              content={getTranslation('Cancel')}
              style={{ flex: 1 }}
              onClick={() => {
                setShowRejectInput(false);
                setRejectReason('');
              }}
            />
            <Spacer.Horizontal size={12} />
            <Button.DestructiveSolid
              variant="bold"
              align="center"
              active={rejectReason.length < MAX_REJECTION_NOTE_LENGTH}
              style={{ flex: 3 }}
              onClick={() => {
                if (rejectReason.trim().length === 0) {
                  alert(getTranslation('Please add a reason for the rejection.'));
                  return;
                }
                rejectReport(report.key, rejectReason.trim());
                setShowRejectInput(false);
                setRejectReason('');
              }}
            >
              <Icon name="send-outline" size={18} color={theme.colors.buttonWhite} />
              <Spacer.Horizontal size={6} />
              <OpenSans.Custom
                size="m-18"
                weight="bold-700"
                style={{ color: theme.colors.buttonWhite }}
              >
                {getTranslation('Submit Rejection')}
              </OpenSans.Custom>
            </Button.DestructiveSolid>
          </Layout.Row>
        </>
      ) : null}

      {report.approvalStatus === 'rejected' && !isManagerOrAdmin && requiresApproval ? (
        <>
          <Spacer.Vertical size={8} />
          <Layout.PressableRow bg="transparent" onClick={() => resubmitReport()}>
            <Icon name="arrow-undo-outline" size={16} color={theme.colors.brandPrimary} />
            <Spacer.Horizontal size={4} />
            <OpenSans.Pressable size="s-16">{getTranslation('Resubmit')}</OpenSans.Pressable>
          </Layout.PressableRow>
        </>
      ) : null}

      {report.approvalStatus === 'approved' && needsReimbursement ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Column px py radius bg="warningXLight" border={[1, 'solid', 'warning']}>
            <Layout.Row align>
              <Icon name="cash-outline" size={20} color={theme.colors.warningDark} />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.warningDark }}>
                {formatCurrency(reimbursementDueTotal)} {getTranslation('requires reimbursement')}
              </OpenSans.Primary>
            </Layout.Row>
            <Spacer.Vertical size={4} />
            <OpenSans.Secondary size="xs-12">
              {getTranslation('Owed to')} {memberName(report.createdBy)}
              {' · '}
              {unreimbursedExpenses.length}{' '}
              {unreimbursedExpenses.length === 1
                ? getTranslation('expense')
                : getTranslation('expenses')}
            </OpenSans.Secondary>
            {isManagerOrAdmin ? (
              <>
                <Spacer.Vertical size={12} />
                <Button.Success
                  variant="bold"
                  py={12}
                  align="center"
                  style={{ width: '100%' }}
                  onClick={() => reimburseReport()}
                >
                  <Icon name="checkmark-outline" size={18} color={theme.colors.buttonWhite} />
                  <Spacer.Horizontal size={6} />
                  <OpenSans.Custom
                    size="m-18"
                    weight="bold-700"
                    style={{ color: theme.colors.buttonWhite }}
                  >
                    {getTranslation('Mark Report Reimbursed')}
                  </OpenSans.Custom>
                </Button.Success>
              </>
            ) : null}
          </Layout.Column>
        </>
      ) : null}

      {report.approvalStatus === 'approved' && hasBeenReimbursed ? (
        <>
          <Spacer.Vertical size={12} />
          <Layout.Column px py radius bg="successXLight" border={[1, 'solid', 'success']}>
            <Layout.Row align>
              <Icon name="checkmark-circle" size={18} color={theme.colors.success} />
              <Spacer.Horizontal size={8} />
              <OpenSans.Primary weight="bold-700" style={{ color: theme.colors.successDark }}>
                {formatCurrency(
                  reimbursableExpenses.reduce((sum, expense) => sum + Math.abs(expense.total), 0),
                )}{' '}
                {getTranslation('reimbursed')}
              </OpenSans.Primary>
            </Layout.Row>
            {reimbursementSource ? (
              <>
                <Spacer.Vertical size={4} />
                <OpenSans.Secondary size="xs-12">
                  {getTranslation('Paid by')} {memberName(reimbursementSource.reimbursedBy)}{' '}
                  {getTranslation('on')} {formatShortDate(reimbursementSource.reimbursedAt)}
                  {' · '}
                  {reimbursableExpenses.length}{' '}
                  {reimbursableExpenses.length === 1
                    ? getTranslation('expense')
                    : getTranslation('expenses')}
                </OpenSans.Secondary>
              </>
            ) : null}
          </Layout.Column>
          {isManagerOrAdmin && wasReimbursedViaReport ? (
            <>
              <Spacer.Vertical size={8} />
              <Layout.PressableRow bg="transparent" onClick={() => undoReportReimbursement()}>
                <Icon name="arrow-undo-outline" size={16} color={theme.colors.secondary} />
                <Spacer.Horizontal size={4} />
                <OpenSans.Secondary size="s-16">
                  {getTranslation('Undo reimbursement')}
                </OpenSans.Secondary>
              </Layout.PressableRow>
            </>
          ) : null}
        </>
      ) : null}
    </Layout.Column>
  );
};
